import { createSlice } from '@reduxjs/toolkit';
import client from '../../../api/apiClient';
import { BalanceTransactionDetails } from 'types/wallet';
import { notify } from 'components/Common/Toast';
import { fetchMerchantBalance } from './merchantBalanceSlice';
import { RootState } from 'redux/store';
import { getDefaultCompanyId } from 'utils/helpers';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';

export enum MONEY_CONTAINER_TYPE {
    BANK_ACCOUNT = 'BANK_ACCOUNT',
    CC = 'CC',
    SVA = 'SVA',
    LOYALTY = 'LOYALTY',
}

export const getAttribute = (moneyContainer: any, key: 'attributes' | 'attributesSecureRetrievable', attr: string) => {
    if (!moneyContainer?.[key]) return '';
    return moneyContainer[key]?.find((a: any) => a.key === attr)?.value || '';
};

export const getLabel = (moneyContainer: MoneyContainer, separator: string = '-') => {
    if (!moneyContainer?.type) return '';
    let name;
    switch (moneyContainer.type) {
        case MONEY_CONTAINER_TYPE.BANK_ACCOUNT:
            name = getAttribute(moneyContainer, 'attributes', 'INSTITUTION_NAME');
            break;
        case MONEY_CONTAINER_TYPE.CC:
        case MONEY_CONTAINER_TYPE.SVA:
            name = getAttribute(moneyContainer, 'attributes', 'ACCOUNT_NAME');
            break;
        default:
            name = '';
            break;
    }
    return `${name} ${separator || '-'} ${moneyContainer.last4AccountDigits}`;
};

export const withdrawToBank = createAsyncThunkWrapper<any, BalanceTransactionDetails>(
    'wallet/withdrawToBank',
    async (trxDetails: BalanceTransactionDetails, { dispatch }) => {
        const otherFields = trxDetails?.fields || [];
        const currencyCode = trxDetails?.currencyCode || '840';
        const transactionBody = {
            details: {
                name: 'CORE',
                details: {
                    name: trxDetails.transactionType,
                    field: [
                        {
                            key: 'CURRENCY_CODE',
                            value: currencyCode,
                        },
                        {
                            key: 'AMOUNT',
                            value: trxDetails.amount,
                        },
                        {
                            key: 'FROM_ACCOUNT',
                            value: trxDetails.from,
                        },
                        {
                            key: 'TO_ACCOUNT',
                            value: 'to' in trxDetails && trxDetails.to,
                        },
                        ...otherFields,
                    ],
                },
            },
        };
        const response = (await client.post('user/executeTrx', transactionBody)) as any;
        const additionalStatusCode = response?.contextResponse?.additionalStatusCode;
        if (response?.code === 200 || response?.contextResponse?.statusCode === 'SUCCESS') {
            notify('Funds transferred successfully', 'success');
            dispatch(fetchMerchantBalance());
        } else if (additionalStatusCode) {
            notify(additionalStatusCode, 'error');
        }
        return response;
    }
);
//
interface Attribute {
    key: string;
    value: string;
}

interface SecureRetrievableAttribute {
    key: string;
    value: string;
}

interface MoneyContainer {
    personId?: string;
    last4AccountDigits: string;
    currency: string;
    status: string;
    createdDate: string;
    lastUpdatedDate: string;
    mozidoId: number;
    containerId: number;
    type: string;
    subType?: string;
    description?: string;
    attributes: Attribute[];
    attributesSecureRetrievable?: SecureRetrievableAttribute[];
    default?: boolean;
}

interface MoneyContainerState {
    records: MoneyContainer[];
    defaultSVA: MoneyContainer;
    error?: string | undefined;
    loading: boolean;
}
const sva: MoneyContainer = {
    personId: '',
    last4AccountDigits: '',
    currency: '840',
    status: '',
    createdDate: '',
    lastUpdatedDate: '',
    mozidoId: 0,
    containerId: 0,
    type: 'SVA',
    attributes: [
        {
            key: 'ACCOUNT_NAME',
            value: '',
        },
    ],
    default: false,
};

const initialState: MoneyContainerState = {
    records: [],
    defaultSVA: sva,
    loading: false,
};

export const fetchMoneyContainer = createAsyncThunkWrapper('wallet/fetchMoneyContainer', async (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    const entityId = getDefaultCompanyId(state);
    const response: any = await client.post('/user/getMoneyContainers', {
        status: 'ACTIVE',
        entityId,
    });
    const { moneyContainers = [] } = response || {};
    const { payload } = await dispatch(getDefaultSvaOfTheMerchant());
    return { moneyContainers, defaultSVA: payload };
});

export const getDefaultSvaOfTheMerchant = createAsyncThunkWrapper('wallet/getDefaultSVAOfTheMerchant', async (_, { getState }) => {
    const state = getState() as RootState;
    const companyId = getDefaultCompanyId(state);
    if (companyId) {
        const res: any = await client.get('/merchant/getCompanySva', { headers: { companyId } });
        return res?.sva;
    } else {
        throw new Error('No company id found');
    }
});

const moneyContainerSlice = createSlice({
    name: 'moneyContainerSlice',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchMoneyContainer.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(fetchMoneyContainer.fulfilled, (state, action) => {
            state.loading = false;
            const payload = action?.payload;
            if (!payload) return;
            const { moneyContainers, defaultSVA } = payload || {};
            state.records = moneyContainers.map((mc: any) => ({ ...mc, label: getLabel(mc) }));
            if (defaultSVA) {
                state.defaultSVA = defaultSVA;
            }
        });
        builder.addCase(fetchMoneyContainer.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message;
        });
    },
});

export default moneyContainerSlice.reducer;
